import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/base/Button';
import Input from '@/components/base/Input';
import Select from '@/components/base/Select';
import Alert from '@/components/base/Alert';
import { adminApi } from '@/lib/adminApi';
import type { AdminValidationCode } from '@/lib/adminApi';
import { formatDate } from '@/lib/format';
import { AdminCard, EmptyBlock, ErrorBlock, LoadingBlock, TableCell, TableHeaderCell } from './ui';

const OPERATIONS = ['withdrawal', 'settings', 'login'] as const;

export default function CodesTab({ token }: { token: string }) {
  const { t, i18n } = useTranslation();
  const [codes, setCodes] = useState<AdminValidationCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [generated, setGenerated] = useState('');
  const [form, setForm] = useState({
    operationType: 'withdrawal',
    operationRef: '',
    validityMinutes: '30',
    maxAttempts: '2',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const res = await adminApi.codesList(token);
      if (!res.ok) {
        setFailed(true);
        return;
      }
      setCodes((res.codes ?? []) as AdminValidationCode[]);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const generate = async () => {
    if (busy) return;
    setBusy(true);
    setGenerated('');
    const res = await adminApi.codeCreate(token, {
      operationType: form.operationType,
      operationRef: form.operationRef.trim(),
      validityMinutes: Number(form.validityMinutes || 30),
      maxAttempts: Number(form.maxAttempts || 2),
    });
    setBusy(false);
    if (res.ok && typeof res.code === 'string') {
      setGenerated(res.code);
      setForm({ ...form, operationRef: '' });
      load();
    }
  };

  const revoke = async (codeId: number) => {
    setBusy(true);
    await adminApi.codeRevoke(token, codeId);
    setBusy(false);
    load();
  };

  const statusOf = (row: AdminValidationCode) => {
    if (row.usedAt) return 'used';
    if (row.expired) return 'expired';
    return 'active';
  };

  const statusStyle: Record<string, string> = {
    active: 'bg-primary-50 text-primary-800 border-primary-200',
    used: 'bg-background-200 text-foreground-600 border-background-300',
    expired: 'bg-accent-50 text-accent-800 border-accent-200',
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-5 items-start">
      <AdminCard title={t('admin.codes.title')} subtitle={t('admin.codes.subtitle')} padded={false}>
        {loading ? (
          <LoadingBlock />
        ) : failed ? (
          <ErrorBlock onRetry={load} />
        ) : codes.length === 0 ? (
          <EmptyBlock label={t('admin.codes.empty')} icon="ri-shield-keyhole-line" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px]">
              <thead className="bg-background-100">
                <tr>
                  <TableHeaderCell>{t('admin.codes.code')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.codes.operation')}</TableHeaderCell>
                  <TableHeaderCell align="right">{t('admin.codes.attempts')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.codes.expires')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.common.status')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.common.actions')}</TableHeaderCell>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {codes.map((row) => {
                  const status = statusOf(row);
                  return (
                    <tr key={row.id} className="hover:bg-background-100/60 transition-colors">
                      <TableCell>
                        <span className="font-label font-semibold tracking-[0.16em] text-foreground-950">
                          {row.code}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="block text-[12.5px] text-foreground-800">
                          {t(`admin.codes.type.${row.operationType}`)}
                        </span>
                        <span className="block text-[11px] text-foreground-400 font-label">
                          {row.operationRef ?? '—'}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span className="text-foreground-700">
                          {row.attempts}/{row.maxAttempts}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-foreground-500 whitespace-nowrap">
                          {formatDate(row.expiresAt, i18n.language, true)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold font-label ${statusStyle[status]}`}
                        >
                          {t(`admin.codes.state.${status}`)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {status === 'active' ? (
                          <button
                            type="button"
                            onClick={() => revoke(row.id)}
                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-background-300 text-[11.5px] font-semibold text-foreground-700 hover:border-accent-400 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            {t('admin.codes.revoke')}
                          </button>
                        ) : (
                          <span className="text-[12px] text-foreground-400">—</span>
                        )}
                      </TableCell>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </AdminCard>

      <AdminCard title={t('admin.codes.generate')} subtitle={t('admin.codes.generateHint')}>
        <div className="flex flex-col gap-3.5">
          <Select
            label={t('admin.codes.operation')}
            name="operationType"
            value={form.operationType}
            onChange={(event) => setForm({ ...form, operationType: event.target.value })}
            options={OPERATIONS.map((op) => ({ value: op, label: t(`admin.codes.type.${op}`) }))}
          />
          <Input
            label={t('admin.codes.ref')}
            name="operationRef"
            value={form.operationRef}
            onChange={(event) => setForm({ ...form, operationRef: event.target.value })}
            placeholder="MX-2026-0141"
            icon="ri-hashtag"
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label={t('admin.codes.validity')}
              name="validityMinutes"
              type="number"
              min="1"
              value={form.validityMinutes}
              onChange={(event) => setForm({ ...form, validityMinutes: event.target.value })}
            />
            <Input
              label={t('admin.codes.attempts')}
              name="maxAttempts"
              type="number"
              min="1"
              value={form.maxAttempts}
              onChange={(event) => setForm({ ...form, maxAttempts: event.target.value })}
            />
          </div>
          <Button fullWidth loading={busy} onClick={generate} icon={<i className="ri-shield-keyhole-line" aria-hidden="true" />}>
            {t('admin.codes.generate')}
          </Button>

          {generated ? (
            <Alert tone="success" title={t('admin.codes.created')}>
              <span className="font-label font-semibold tracking-[0.2em]">{generated}</span>
            </Alert>
          ) : null}
        </div>
      </AdminCard>
    </div>
  );
}